import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import PageHeader from "../../../components/PageHeader";
import Button from "../../../ui/components/Button";
import { useIsAdminViewMode } from "../../../hooks/useViewMode";
import { getCorporationData } from "../../../services/corporation";
import axiosInstance from "../../../services/axiosInstance";
import ConstantsFormPanel from "../ConstantsFormPanel";

const BASE_URL_DEFAULT = (
  process.env.REACT_APP_API_BACKEND || "https://backend.com"
).replace(/\/$/, "");
const LOCAL_OVERRIDE_URL = "http://localhost:5000";

function formatBodyForDisplay(body) {
  if (body == null) return null;
  if (typeof body !== "string") return JSON.stringify(body, null, 2);
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function RequestBlock({ request, headersLabel, bodyLabel }) {
  if (!request) return null;
  const bodyFormatted =
    request.body != null ? formatBodyForDisplay(request.body) : null;
  return (
    <div>
      <p className="settings-api__request-line">
        <span className="settings-api__method">{request.method}</span>{" "}
        {request.url}
      </p>
      <p className="settings-api__meta-label">{headersLabel}</p>
      <pre className="settings-api__code">
        {JSON.stringify(request.headers, null, 2)}
      </pre>
      {bodyFormatted != null && (
        <>
          <p className="settings-api__meta-label">{bodyLabel}</p>
          <pre className="settings-api__code">{bodyFormatted}</pre>
        </>
      )}
    </div>
  );
}

function ResponseRaw({ data, error, status, ok }) {
  if (error) {
    return (
      <pre className="settings-api__code settings-api__code--error">
        {String(error)}
      </pre>
    );
  }
  return (
    <pre className="settings-api__code">
      {status != null ? `HTTP ${status}${ok ? " OK" : ""}\n` : ""}
      {data == null ? "" : JSON.stringify(data, null, 2)}
    </pre>
  );
}

function ExchangeDetails({ request, response, t }) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (request || response) {
      setOpen(true);
    }
  }, [request, response]);

  if (!request && !response) return null;

  return (
    <details
      className="settings-api__details"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="settings-api__details-summary">
        {t("corp_api_exchange")}
      </summary>
      <div className="settings-api__details-body">
        {request && (
          <>
            <p className="settings-api__meta-label">{t("corp_api_request")}</p>
            <RequestBlock
              request={request}
              headersLabel={t("corp_api_headers")}
              bodyLabel={t("corp_api_body")}
            />
          </>
        )}
        {response && (
          <>
            <p className="settings-api__meta-label">{t("corp_api_response")}</p>
            <ResponseRaw {...response} />
          </>
        )}
      </div>
    </details>
  );
}

function ApiTextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  readOnly = false,
  placeholder,
  actions = null,
}) {
  const field = (
    <div className="settings-constants-suffix-field">
      <input
        id={id}
        type={type}
        className="settings-constants-suffix-field__input"
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="settings-constants-field-group">
      <label className="settings-api__field-label" htmlFor={id}>
        {label}
      </label>
      {actions ? (
        <div className="settings-api__field-row">
          {field}
          {actions}
        </div>
      ) : (
        field
      )}
    </div>
  );
}

function EndpointHeader({ method, path }) {
  return (
    <div className="settings-api__endpoint">
      <span className="settings-api__method">{method}</span>
      <code className="settings-api__path">{path}</code>
    </div>
  );
}

const SettingsApi = () => {
  const { t } = useTranslation();
  const isAkanthasAdmin = useIsAdminViewMode();
  const [useLocalOverride, setUseLocalOverride] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [areaId, setAreaId] = useState("");
  const [listPeriodStart, setListPeriodStart] = useState("");
  const [listPeriodStop, setListPeriodStop] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodStop, setPeriodStop] = useState("");

  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenRequest, setTokenRequest] = useState(null);
  const [tokenResponse, setTokenResponse] = useState(null);

  const [areasLoading, setAreasLoading] = useState(false);
  const [areasRequest, setAreasRequest] = useState(null);
  const [areasResponse, setAreasResponse] = useState(null);

  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsRequest, setReportsRequest] = useState(null);
  const [reportsResponse, setReportsResponse] = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailResponse, setDetailResponse] = useState(null);

  const [credsLoading, setCredsLoading] = useState(false);
  const [credsResponse, setCredsResponse] = useState(null);

  const apiBase = useMemo(() => {
    if (isAkanthasAdmin && useLocalOverride) {
      return LOCAL_OVERRIDE_URL;
    }
    return BASE_URL_DEFAULT;
  }, [isAkanthasAdmin, useLocalOverride]);

  useEffect(() => {
    if (!isAkanthasAdmin && useLocalOverride) {
      setUseLocalOverride(false);
    }
  }, [isAkanthasAdmin, useLocalOverride]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCorporationData();
        const corpId = res?.data?._id || res?.data?.corporation?._id;
        if (!cancelled && corpId) {
          setClientId(String(corpId));
        }
      } catch {
        // Ignore — user can paste client_id manually.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copyValue = useCallback(
    async (value) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        toast.success(t("corp_api_copied"));
      } catch {
        toast.error(t("corp_api_copy_failed"));
      }
    },
    [t],
  );

  const authHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  });

  const getToken = async () => {
    setTokenLoading(true);
    setTokenResponse(null);
    const url = `${apiBase}/oauth/token`;
    const body = {
      client_id: clientId,
      client_secret: clientSecret,
      grant_type: "client_credentials",
    };
    setTokenRequest({
      method: "POST",
      url,
      headers: { "Content-Type": "application/json" },
      body,
    });
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => null);
      setTokenResponse({ ok: r.ok, status: r.status, data });
      if (r.ok && data?.access_token) {
        setAccessToken(data.access_token);
      }
    } catch (e) {
      setTokenResponse({ error: e.message || "Network error" });
    } finally {
      setTokenLoading(false);
    }
  };

  const callGet = async ({
    path,
    setLoading,
    setRequest,
    setResponse,
    onSuccess,
  }) => {
    setLoading(true);
    setResponse(null);
    const url = `${apiBase}${path}`;
    const headers = authHeaders(accessToken);
    setRequest({ method: "GET", url, headers, body: null });
    try {
      const r = await fetch(url, { method: "GET", headers });
      const data = await r.json().catch(() => null);
      setResponse({ ok: r.ok, status: r.status, data });
      if (r.ok && onSuccess) onSuccess(data);
    } catch (e) {
      setResponse({ error: e.message || "Network error" });
    } finally {
      setLoading(false);
    }
  };

  const generateCredentials = async () => {
    setCredsLoading(true);
    setCredsResponse(null);
    try {
      const r = await axiosInstance.post(
        "corporation/generate-oauth2-credentials",
      );
      setCredsResponse({ ok: true, data: r.data });
      if (r.data?.client_id) setClientId(String(r.data.client_id));
      if (r.data?.client_secret) {
        setClientSecret(String(r.data.client_secret));
        setShowSecret(true);
      }
    } catch (e) {
      setCredsResponse({
        ok: false,
        error:
          e?.response?.data?.error ||
          e?.message ||
          t("corp_api_generate_failed"),
      });
    } finally {
      setCredsLoading(false);
    }
  };

  if (!Cookies.get("userToken")) {
    return null;
  }

  return (
    <div className="settings-page-wrapper">
      <PageHeader title={t("corp_api_nav_label")} />
      <div className="settings-content-wrapper">
        <div className="settings-constants-form">
          <div className="settings-constants-form__column">
            <div className="settings-api__intro">
              <p>{t("corp_api_intro_1")}</p>
              <p>{t("corp_api_intro_2")}</p>
              <p>{t("corp_api_flow_hint")}</p>
            </div>

            <h3 className="settings-constants-section-title">
              {t("corp_api_section_access")}
            </h3>

            <ConstantsFormPanel title={t("corp_api_credentials_title")}>
              <p className="settings-constants-help-text">
                {t("corp_api_credentials_desc")}
              </p>
              <div className="settings-api__actions">
                <Button
                  variant="primary"
                  onClick={generateCredentials}
                  disabled={credsLoading}
                >
                  {credsLoading
                    ? t("corp_api_loading")
                    : t("corp_api_generate_credentials")}
                </Button>
              </div>
              <ApiTextField
                id="corp-api-client-id"
                label={t("corp_api_client_id")}
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
                actions={
                  <Button
                    variant="secondary"
                    onClick={() => copyValue(clientId)}
                    disabled={!clientId}
                  >
                    {t("Copy")}
                  </Button>
                }
              />
              <ApiTextField
                id="corp-api-client-secret"
                label={t("corp_api_client_secret")}
                value={clientSecret}
                type={showSecret ? "text" : "password"}
                onChange={(event) => setClientSecret(event.target.value)}
                actions={
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setShowSecret((prev) => !prev)}
                      disabled={!clientSecret}
                    >
                      {showSecret
                        ? t("corp_api_hide_secret")
                        : t("corp_api_show_secret")}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => copyValue(clientSecret)}
                      disabled={!clientSecret}
                    >
                      {t("Copy")}
                    </Button>
                  </>
                }
              />
              {credsResponse && (
                <details className="settings-api__details">
                  <summary className="settings-api__details-summary">
                    {t("corp_api_response")}
                  </summary>
                  <div className="settings-api__details-body">
                    <ResponseRaw
                      data={credsResponse.data}
                      error={credsResponse.error}
                      ok={credsResponse.ok}
                    />
                  </div>
                </details>
              )}
            </ConstantsFormPanel>

            <ConstantsFormPanel title={t("corp_api_connection_title")}>
              <ApiTextField
                id="corp-api-base-url"
                label={t("corp_api_base_url")}
                value={apiBase}
                readOnly
                actions={
                  <Button
                    variant="secondary"
                    onClick={() => copyValue(apiBase)}
                    disabled={!apiBase}
                  >
                    {t("Copy")}
                  </Button>
                }
              />
              {isAkanthasAdmin ? (
                <div className="settings-api__actions">
                  <Button
                    variant="secondary"
                    onClick={() => setUseLocalOverride((prev) => !prev)}
                  >
                    {useLocalOverride
                      ? t("corp_api_use_default_backend")
                      : t("corp_api_use_local_backend")}
                  </Button>
                </div>
              ) : null}
              <p className="settings-constants-help-text">
                {t("corp_api_auth_desc")}
              </p>
              <div className="settings-api__actions">
                <Button
                  variant="primary"
                  onClick={getToken}
                  disabled={tokenLoading || !clientId || !clientSecret}
                >
                  {tokenLoading
                    ? t("corp_api_loading")
                    : t("corp_api_get_token")}
                </Button>
              </div>
              <p
                className={`settings-api__status ${
                  accessToken
                    ? "settings-api__status--ok"
                    : "settings-api__status--idle"
                }`}
              >
                {accessToken
                  ? t("corp_api_token_active")
                  : t("corp_api_token_none")}
              </p>
              <ExchangeDetails
                request={tokenRequest}
                response={tokenResponse}
                t={t}
              />
            </ConstantsFormPanel>

            <h3 className="settings-constants-section-title">
              {t("corp_api_section_explorer")}
            </h3>

            <ConstantsFormPanel title={t("corp_api_areas_title")}>
              <EndpointHeader
                method="GET"
                path="/api/oauth2/characterization/areas"
              />
              <div className="settings-api__actions">
                <Button
                  variant="primary"
                  onClick={() =>
                    callGet({
                      path: "/api/oauth2/characterization/areas",
                      setLoading: setAreasLoading,
                      setRequest: setAreasRequest,
                      setResponse: setAreasResponse,
                      onSuccess: (data) => {
                        const first = data?.areas?.[0]?.containerMongoId;
                        if (first) setAreaId(String(first));
                      },
                    })
                  }
                  disabled={areasLoading || !accessToken}
                >
                  {areasLoading ? t("corp_api_loading") : t("corp_api_try")}
                </Button>
              </div>
              <ExchangeDetails
                request={areasRequest}
                response={areasResponse}
                t={t}
              />
            </ConstantsFormPanel>

            <ConstantsFormPanel title={t("corp_api_reports_title")}>
              <p className="settings-constants-help-text">
                {t("corp_api_reports_desc")}
              </p>
              <p className="settings-constants-help-text">
                {t("corp_api_reports_window_hint")}
              </p>
              <p className="settings-constants-help-text">
                {t("corp_api_intro_3")}
              </p>
              <EndpointHeader
                method="GET"
                path="/api/oauth2/characterization/areas/:id/reports"
              />
              <ApiTextField
                id="corp-api-area-id"
                label={t("corp_api_area_id")}
                value={areaId}
                onChange={(event) => setAreaId(event.target.value)}
              />
              <ApiTextField
                id="corp-api-list-period-start"
                label={t("corp_api_list_period_start")}
                value={listPeriodStart}
                onChange={(event) => setListPeriodStart(event.target.value)}
                placeholder="2025-01-01T00:00:00.000Z"
              />
              <ApiTextField
                id="corp-api-list-period-stop"
                label={t("corp_api_list_period_stop")}
                value={listPeriodStop}
                onChange={(event) => setListPeriodStop(event.target.value)}
                placeholder="2026-08-13T00:00:00.000Z"
              />
              <div className="settings-api__actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (listPeriodStart) {
                      params.set("periodStart", listPeriodStart);
                    }
                    if (listPeriodStop) {
                      params.set("periodStop", listPeriodStop);
                    }
                    const qs = params.toString();
                    callGet({
                      path: `/api/oauth2/characterization/areas/${encodeURIComponent(
                        areaId,
                      )}/reports${qs ? `?${qs}` : ""}`,
                      setLoading: setReportsLoading,
                      setRequest: setReportsRequest,
                      setResponse: setReportsResponse,
                      onSuccess: (data) => {
                        const first = data?.reports?.[0];
                        if (first?.periodStart) {
                          setPeriodStart(first.periodStart);
                        }
                        if (first?.periodStop) {
                          setPeriodStop(first.periodStop);
                        }
                      },
                    });
                  }}
                  disabled={reportsLoading || !accessToken || !areaId}
                >
                  {reportsLoading ? t("corp_api_loading") : t("corp_api_try")}
                </Button>
              </div>
              <ExchangeDetails
                request={reportsRequest}
                response={reportsResponse}
                t={t}
              />
            </ConstantsFormPanel>

            <ConstantsFormPanel title={t("corp_api_detail_title")}>
              <p className="settings-constants-help-text">
                {t("corp_api_detail_desc")}
              </p>
              <EndpointHeader
                method="GET"
                path="/api/oauth2/characterization/areas/:id/reports/detail"
              />
              <ApiTextField
                id="corp-api-period-start"
                label={t("corp_api_period_start")}
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
              />
              <ApiTextField
                id="corp-api-period-stop"
                label={t("corp_api_period_stop")}
                value={periodStop}
                onChange={(event) => setPeriodStop(event.target.value)}
              />
              <div className="settings-api__actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    const qs = new URLSearchParams({
                      periodStart,
                      periodStop,
                    }).toString();
                    callGet({
                      path: `/api/oauth2/characterization/areas/${encodeURIComponent(areaId)}/reports/detail?${qs}`,
                      setLoading: setDetailLoading,
                      setRequest: setDetailRequest,
                      setResponse: setDetailResponse,
                    });
                  }}
                  disabled={
                    detailLoading ||
                    !accessToken ||
                    !areaId ||
                    !periodStart ||
                    !periodStop
                  }
                >
                  {detailLoading ? t("corp_api_loading") : t("corp_api_try")}
                </Button>
              </div>
              <ExchangeDetails
                request={detailRequest}
                response={detailResponse}
                t={t}
              />
            </ConstantsFormPanel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsApi;
